/**
 * 标准相切配合几何辅助函数。
 *
 * 文件职责：
 * 1. 集中定义 Tangent Mate 使用的圆形半径语义；
 * 2. 提供圆锥面与球相切的可复用几何计算；
 * 3. 保持纯函数输入输出，不依赖装配文档、UI 或 Three.js 场景对象。
 */

import * as THREE from 'three';
import {
  MateEntityKind,
  type MateEntityRef,
} from '../../definition';
import {
  normalizeVector,
  type AxisGeometry,
  type ConeGeometry,
} from './StandardMateGeometryAccess';

/**
 * 圆锥面到球心的有符号距离结果。
 */
export interface ConeSphereSignedDistanceResult {
  /** 指向圆锥面外侧的单位法向。 */
  readonly normal: THREE.Vector3;
  /** 球心到圆锥理论侧面的有符号距离。 */
  readonly signedDistance: number;
}

/**
 * 为方向构造稳定的垂直方向。
 *
 * @param direction 源方向。
 * @param epsilon 容差。
 * @returns 垂直方向。
 */
function resolveStablePerpendicularDirection(direction: THREE.Vector3, epsilon: number): THREE.Vector3 {
  const seed = Math.abs(direction.x) < 0.9
    ? new THREE.Vector3(1, 0, 0)
    : new THREE.Vector3(0, 1, 0);
  const perpendicular = direction.clone().cross(seed);
  return normalizeVector(perpendicular, epsilon) ?? new THREE.Vector3(0, 0, 1);
}

/**
 * 读取可用于相切距离的轴线半径。
 *
 * 设计说明：
 * 1. 圆柱面、圆边使用自身半径；
 * 2. 直线和基准轴在 SOLIDWORKS Tangent 中作为零半径轴线参与 Line-Sphere 等组合；
 * 3. 圆锥面的半径随截面变化，不能在这里伪装为固定半径，否则会错误求解。
 *
 * @param entity 实体引用。
 * @param axis 轴线几何。
 * @returns 相切半径；不可按固定半径求解时返回 null。
 */
export function resolveAxisTangentRadius(entity: MateEntityRef, axis: AxisGeometry): number | null {
  if (typeof axis.radius === 'number' && Number.isFinite(axis.radius)) {
    return axis.radius;
  }
  if (entity.entityKind === MateEntityKind.Line || entity.entityKind === MateEntityKind.ReferenceAxis) {
    return 0;
  }
  return null;
}

/**
 * 解析圆锥理论侧面相对球心的有符号距离。
 *
 * 设计说明：
 * 圆锥半角为 `alpha` 时，任意过轴截面内侧面与轴线夹角为 `alpha`。
 * 将球心分解为轴向距离 `s` 与径向距离 `r` 后，侧面法向可写为
 * `radial * cos(alpha) - axis * sin(alpha)`，其点积就是到无限圆锥侧面的有符号距离。
 *
 * @param cone 圆锥几何。
 * @param sphereCenter 球心。
 * @param epsilon 容差。
 * @returns 有符号距离；圆锥参数退化时返回 null。
 */
export function resolveConeSphereSignedDistance(
  cone: ConeGeometry,
  sphereCenter: THREE.Vector3,
  epsilon: number,
): ConeSphereSignedDistanceResult | null {
  if (!Number.isFinite(cone.halfAngle) || cone.halfAngle <= 0 || cone.halfAngle >= Math.PI / 2) {
    return null;
  }

  const apexToCenter = sphereCenter.clone().sub(cone.apex);
  const axialDistance = apexToCenter.dot(cone.direction);
  const radial = apexToCenter
    .clone()
    .sub(cone.direction.clone().multiplyScalar(axialDistance));
  const radialDirection = normalizeVector(radial, epsilon)
    ?? resolveStablePerpendicularDirection(cone.direction, epsilon);
  const normal = radialDirection
    .multiplyScalar(Math.cos(cone.halfAngle))
    .sub(cone.direction.clone().multiplyScalar(Math.sin(cone.halfAngle)))
    .normalize();

  return {
    normal,
    signedDistance: apexToCenter.dot(normal),
  };
}
